import React, { forwardRef, useRef } from 'react';
import { GalleryTileWithRef } from '../../../../GalleryTile';
import { Skeleton } from '@shared/components/Skeleton';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsPublicGallerySsr } from '../../../useIsPublicGallerySsr';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useNearViewport } from '@shared/hooks/useNearViewport';
import { desktopAspect, mobileAspect, tileRowGap, galleryDesktopAspect, galleryMobileAspect } from '../../CardsConfig';
import { CardTile } from './CardTile';
import { CardTileStatic } from './CardTileStatic';
import type { CardTileProps } from './CardTile.types';

type CardTileSkeletonProps = Readonly<{
  aspectRatio: string;
  isMobile: boolean;
}>;

/** Tiles at or above this index (two rows at the widest grid) load their image lazily. */
const EAGER_IMAGE_TILE_COUNT = 12;

// Same grid, aspect ratio, and row gap as CardTile, so the row doesn't shift when the tile mounts.
// A single static box where the card preview will sit: a large gallery holds hundreds of these at
// once, and a shimmer on each costs a style recalc per box on every frame, even off screen.
const CardTileSkeleton = forwardRef<HTMLDivElement, CardTileSkeletonProps>(function CardTileSkeleton({ aspectRatio, isMobile }, ref) {
  return (
    <GalleryTileWithRef
      ref={ref}
      aspectRatio={aspectRatio}
      alignContent="start"
      rowGap={tileRowGap}
    >
      <Skeleton
        disableShimmerAnimation
        // Matches AssetPreviewFrame dimensions.
        aspectRatio={isMobile ? '8 / 10' : '9 / 10'}
      />
    </GalleryTileWithRef>
  );
});

/**
 * Defers mounting the whole tile (swatches and their tooltips, colorway resolution, favorite
 * badge, impression observer) until it is near the viewport, so a large gallery page only pays for
 * the tiles the user scrolls to. Until then the server-rendered public gallery shows
 * `CardTileStatic` — the link and print thumbnail a crawler reads from the server HTML
 * (PRNT-5494) — and every other gallery a skeleton, so the admin gallery never shows a
 * stock-name image before the couple's names.
 */
export const CardTileShell = (props: CardTileProps) => {
  const { isSuperAdmin } = useAuth();
  const isPublicGallerySsr = useIsPublicGallerySsr();
  const isMobile = useIsMobileScreen();
  const { ref, hasBeenNear } = useNearViewport({ preloadViewports: 0.4 });
  // Tabbing to a static tile scrolls it into view, which swaps in the live tile and unmounts the
  // focused link; the live tile takes the focus back so keyboard navigation carries on. A ref:
  // it is only read when the swap renders, so focus and blur needn't re-render the shell.
  const hasFocusRef = useRef(false);

  if (!props.data.enabled && !isSuperAdmin) return null;

  // Favorites-gallery tiles show a price label and need the taller aspect.
  const showPrice = props.cardTileSource === 'favoritesGallery';
  const aspectRatio = showPrice ? (isMobile ? mobileAspect : desktopAspect) : isMobile ? galleryMobileAspect : galleryDesktopAspect;

  if (hasBeenNear) {
    return (
      <CardTile
        {...props}
        aspectRatio={aspectRatio}
        focusOnMount={hasFocusRef.current}
      />
    );
  }

  if (isPublicGallerySsr) {
    return (
      <CardTileStatic
        {...props}
        ref={ref}
        aspectRatio={aspectRatio}
        imageLoading={props.index !== undefined && props.index < EAGER_IMAGE_TILE_COUNT ? 'eager' : 'lazy'}
        onFocus={() => {
          hasFocusRef.current = true;
        }}
        onBlur={() => {
          hasFocusRef.current = false;
        }}
      />
    );
  }

  return (
    <CardTileSkeleton
      ref={ref}
      aspectRatio={aspectRatio}
      isMobile={isMobile}
    />
  );
};
